'use client';

import { useRouter } from 'next/navigation';
import React, { useState, useEffect } from 'react';
import { generateRoomId } from '@/lib/client-utils';
import { enableWebPush } from '@/lib/push-client';
import styles from '../styles/Home.module.css';

export default function Page() {
  const router = useRouter();
  const [view, setView] = useState<'lobby' | 'login' | 'dashboard'>('lobby');
  const [name, setName] = useState('');
  const [room, setRoom] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [loginName, setLoginName] = useState('Dr. Craig Coenen');
  const [token, setToken] = useState('');
  const [sessions, setSessions] = useState<any[]>([]);
  const [creating, setCreating] = useState(false);
  const [newPatient, setNewPatient] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newTime, setNewTime] = useState('');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  // Check for stored auth on load
  useEffect(() => {
    const stored = localStorage.getItem('mycra-token');
    if (stored) {
      setToken(stored);
      setView('dashboard');
      loadSessions(stored);
    }
    const savedName = localStorage.getItem('mycra-name');
    if (savedName) setName(savedName);
  }, []);

  async function loginWithPassphrase() {
    try {
      const res = await fetch('/api/auth/passphrase', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passphrase, name: loginName }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      localStorage.setItem('mycra-token', data.token);
      setToken(data.token);
      setView('dashboard');
      loadSessions(data.token);
    } catch (e: any) {
      alert('Login failed: ' + e.message);
    }
  }

  async function loadSessions(authToken: string) {
    try {
      const res = await fetch('/api/sessions', {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch {
      setSessions([]);
    }
  }

  async function createSession() {
    if (!newPatient.trim()) return;
    try {
      const res = await fetch('/api/sessions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          patient_name: newPatient,
          patient_email: newEmail || undefined,
          scheduled_at: newTime || undefined,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setCreating(false);
      setNewPatient('');
      setNewEmail('');
      setNewTime('');
      loadSessions(token);
      // Copy link
      const link = data.session?.link;
      if (link) {
        navigator.clipboard.writeText(link).catch(() => {});
        alert(`Session created! Link copied:\n${link}`);
      }
    } catch (e: any) {
      alert('Failed: ' + e.message);
    }
  }

  function joinLobby() {
    if (!name.trim() || !room.trim()) return;
    localStorage.setItem('mycra-name', name.trim());
    const sanitized = room.trim().replace(/[^a-zA-Z0-9\-_]/g, '-');
    router.push(`/rooms/${sanitized}`);
  }

  async function startSessionFromDash(sessionId: string) {
    try {
      const res = await fetch(`/api/sessions/${sessionId}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.push) {
        const { ios, android, web, errors } = data.push;
        const total = ios + android + web;
        if (total > 0) {
          console.log(`[Push] Notified patient: ${total} device(s)`);
        }
        if (errors?.length) {
          console.warn('[Push] Some errors:', errors);
        }
      }
    } catch (e) {
      console.warn('Push notification failed (session still starting):', e);
    }
    router.push(`/rooms/${sessionId}`);
  }

  function logout() {
    localStorage.removeItem('mycra-token');
    setToken('');
    setView('lobby');
    setSessions([]);
  }

  // ── LOBBY ──
  if (view === 'lobby') {
    return (
      <>
        <main className={styles.main} data-lk-theme="default">
          <div className="mycra-header">
            <div className="brand">Dr. Craig Coenen, Psy.D</div>
            <h1>Your session is ready.</h1>
            <p>Private, secure video — directly in your browser. No app needed.</p>
          </div>
          <div className="mycra-card">
            <input
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
            <input
              type="text"
              placeholder="Appointment code (e.g. monday-3pm)"
              value={room}
              onChange={(e) => setRoom(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && joinLobby()}
            />
            <button className="mycra-btn mycra-btn-primary" onClick={joinLobby}>
              Next
            </button>
            <div className="mycra-privacy">&#128274; Private and encrypted. Nothing is recorded.</div>
            {name.trim() && !pushEnabled && (
              <button
                className="mycra-btn mycra-btn-outline"
                style={{ marginTop: '0.5rem', fontSize: '14px' }}
                disabled={pushLoading}
                onClick={async () => {
                  setPushLoading(true);
                  const userId = `patient-${name.trim().toLowerCase().replace(/\s+/g, '-')}`;
                  const ok = await enableWebPush(userId, name.trim());
                  setPushLoading(false);
                  if (ok) {
                    setPushEnabled(true);
                  }
                }}
              >
                {pushLoading ? 'Setting up...' : 'Notify me when my session starts'}
              </button>
            )}
            {pushEnabled && (
              <div style={{ marginTop: '0.5rem', fontSize: '14px', color: '#22c55e', textAlign: 'center' }}>
                Notifications enabled — we'll ring you when Dr. Coenen is ready.
              </div>
            )}
          </div>
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button
              className="mycra-btn mycra-btn-outline"
              style={{ width: 'auto', padding: '10px 24px', fontSize: '14px' }}
              onClick={() => setView('login')}
            >
              Therapist Login
            </button>
          </div>
        </main>
        <footer>Mycra — Secure Therapy Sessions</footer>
      </>
    );
  }

  // ── LOGIN ──
  if (view === 'login') {
    return (
      <>
        <main className={styles.main} data-lk-theme="default">
          <div className="mycra-header">
            <div className="brand">Mycra</div>
            <h1>Therapist Login</h1>
            <p>Sign in to manage your sessions.</p>
          </div>
          <div className="mycra-card">
            <input
              type="text"
              placeholder="Your name"
              value={loginName}
              onChange={(e) => setLoginName(e.target.value)}
            />
            <input
              type="password"
              placeholder="Enter passphrase"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loginWithPassphrase()}
            />
            <button className="mycra-btn mycra-btn-primary" onClick={loginWithPassphrase}>
              Sign in
            </button>
            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <button
                className="mycra-btn mycra-btn-outline"
                style={{ fontSize: '14px', padding: '10px' }}
                onClick={() => setView('lobby')}
              >
                Back to lobby
              </button>
            </div>
          </div>
        </main>
        <footer>Mycra — Secure Therapy Sessions</footer>
      </>
    );
  }

  // ── DASHBOARD ──
  return (
    <>
      <main data-lk-theme="default" style={{ height: '100%', overflow: 'auto' }}>
        <div className="mycra-dash">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem',
            }}
          >
            <div>
              <h1>Welcome, {loginName.split(' ')[0] || 'Doctor'}</h1>
              <p style={{ color: 'rgba(255,255,255,0.5)', margin: 0, fontSize: '14px' }}>
                Your therapy sessions
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className="mycra-btn mycra-btn-primary"
                style={{ width: 'auto', padding: '10px 20px', fontSize: '14px' }}
                onClick={() => setCreating(true)}
              >
                + New Session
              </button>
              <button
                className="mycra-btn mycra-btn-outline"
                style={{ width: 'auto', padding: '10px 16px', fontSize: '13px' }}
                onClick={logout}
              >
                Sign out
              </button>
            </div>
          </div>

          {/* Quick join */}
          <div
            className="mycra-card"
            style={{ marginBottom: '1.5rem', padding: '1rem', display: 'flex', gap: '8px' }}
          >
            <input
              type="text"
              placeholder="Quick join — enter room code"
              value={room}
              onChange={(e) => setRoom(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && room.trim() && router.push(`/rooms/${room.trim()}`)}
              style={{ marginBottom: 0, flex: 1 }}
            />
            <button
              className="mycra-btn mycra-btn-success"
              style={{ width: 'auto', padding: '12px 20px', fontSize: '15px' }}
              onClick={() => room.trim() && router.push(`/rooms/${room.trim()}`)}
            >
              Join
            </button>
          </div>

          {/* Create session form */}
          {creating && (
            <div
              className="mycra-card"
              style={{ marginBottom: '1.5rem', padding: '1.25rem' }}
            >
              <input
                type="text"
                placeholder="Patient name"
                value={newPatient}
                onChange={(e) => setNewPatient(e.target.value)}
                autoFocus
              />
              <input
                type="text"
                placeholder="Patient email (optional)"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
              <input
                type="datetime-local"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  border: '2px solid rgba(255,255,255,0.15)',
                  borderRadius: '12px',
                  fontSize: '16px',
                  background: 'rgba(255,255,255,0.05)',
                  color: '#fff',
                  marginBottom: '0.75rem',
                }}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="mycra-btn mycra-btn-primary" style={{ flex: 1 }} onClick={createSession}>
                  Create
                </button>
                <button
                  className="mycra-btn mycra-btn-outline"
                  style={{ flex: 1 }}
                  onClick={() => setCreating(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Session list */}
          {sessions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255,255,255,0.4)' }}>
              No sessions yet. Create your first one!
            </div>
          ) : (
            sessions.map((s) => (
              <div key={s.id} className="mycra-session-card">
                <div>
                  <div className="name">{s.patient_name}</div>
                  <div className="meta">
                    {s.scheduled_at
                      ? new Date(s.scheduled_at).toLocaleString([], {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })
                      : 'Anytime'}{' '}
                    &middot; {s.duration_min || 50}min
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span
                    className="mycra-badge"
                    style={{
                      background: 'rgba(37,99,235,0.15)',
                      color: '#60a5fa',
                    }}
                  >
                    {s.status}
                  </span>
                  <button
                    className="mycra-btn mycra-btn-outline"
                    style={{ width: 'auto', padding: '6px 12px', fontSize: '13px' }}
                    onClick={() => {
                      const link = `${window.location.origin}/s/${s.id}?token=${s.magic_token}`;
                      navigator.clipboard.writeText(link);
                      alert('Link copied!');
                    }}
                  >
                    Link
                  </button>
                  <button
                    className="mycra-btn mycra-btn-primary"
                    style={{ width: 'auto', padding: '6px 16px', fontSize: '13px' }}
                    onClick={() => startSessionFromDash(s.id)}
                  >
                    Start
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
      <footer>Mycra — Secure Therapy Sessions</footer>
    </>
  );
}
